import { useState, useCallback, useEffect, useRef } from "react";
import { contactsApi } from "@/api/contacts";
import type { Contact } from "@/types/contact";

export function useContacts() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const initialLoadDone = useRef(false);

  const fetchContacts = useCallback(async () => {
    try {
      if (!initialLoadDone.current) setLoading(true);
      const res = await contactsApi.list();
      setContacts(res.data || []);
      setError(null);
      initialLoadDone.current = true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const createContact = useCallback(
    async (data: Partial<Contact>) => {
      await contactsApi.create(data);
      await fetchContacts();
    },
    [fetchContacts],
  );

  const updateContact = useCallback(
    async (id: number, data: Partial<Contact>) => {
      await contactsApi.update(id, data);
      await fetchContacts();
    },
    [fetchContacts],
  );

  const deleteContact = useCallback(
    async (id: number) => {
      await contactsApi.delete(id);
      await fetchContacts();
    },
    [fetchContacts],
  );

  return {
    contacts,
    loading,
    error,
    refetch: fetchContacts,
    createContact,
    updateContact,
    deleteContact,
  };
}
